import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import {
  LeadActivityOutcomeSchema,
  LeadActivityStateSchema,
  LeadCommunicationDirectionSchema,
  LeadCommunicationTypeSchema,
} from '../lead/leads.schemas';

/**
 * Bus event emitted when a member captures an Owner-Lead. Ids + the
 * source only — no PII — per ADR 0015. `actor` is the member who
 * captured the lead.
 */
export const LeadCreatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  source: z.string(),
  occurredAt: z.iso.datetime(),
});

export type LeadCreatedPayload = z.infer<typeof LeadCreatedPayloadSchema>;

/**
 * Bus event emitted when a member marks an Owner-Lead as Lost (IMM-192).
 * Ids + the loss reason only — the free-text note is PII-adjacent and is
 * intentionally omitted from the audit trail per ADR 0015.
 */
export const LeadLostPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  lossReason: z.string(),
  // The `lead_status_changes` row PK for this transition — the activity-timeline
  // projection (IMM-212) keys its idempotent row on it. New events always set it;
  // nullable models events published before IMM-212 (no id), where the projection
  // falls back to a derived key.
  statusChangeId: z.uuid().nullable(),
  occurredAt: z.iso.datetime(),
});

export type LeadLostPayload = z.infer<typeof LeadLostPayloadSchema>;

/** Bus event emitted when a member archives an Owner-Lead (IMM-192). */
export const LeadArchivedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  // See LeadLostPayloadSchema — the lead_status_changes PK for the timeline projection.
  statusChangeId: z.uuid().nullable(),
  occurredAt: z.iso.datetime(),
});

export type LeadArchivedPayload = z.infer<typeof LeadArchivedPayloadSchema>;

/** Bus event emitted when a member reopens a Lost/Archived Owner-Lead (IMM-192). */
export const LeadReopenedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  // See LeadLostPayloadSchema — the lead_status_changes PK for the timeline projection.
  statusChangeId: z.uuid().nullable(),
  occurredAt: z.iso.datetime(),
});

export type LeadReopenedPayload = z.infer<typeof LeadReopenedPayloadSchema>;

/**
 * Bus event emitted when a member-recorded communication auto-advances a NEW
 * Owner-Lead to CONTACTED (IMM-417). Carries the triggering communication's
 * type + direction so the timeline can render its „automatisch" meta line
 * without re-reading the communication row.
 */
export const LeadContactedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  // See LeadLostPayloadSchema — the lead_status_changes PK for the timeline projection.
  statusChangeId: z.uuid(),
  // Null on an imported CONTACTED: the old system's status change has no triggering communication.
  communicationId: z.uuid().nullable(),
  communicationType: LeadCommunicationTypeSchema.nullable(),
  direction: LeadCommunicationDirectionSchema.nullable(),
  occurredAt: z.iso.datetime(),
});

export type LeadContactedPayload = z.infer<typeof LeadContactedPayloadSchema>;

/**
 * Bus event emitted when a member qualifies an Owner-Lead (IMM-213). Carries
 * the ids of the Contact, Property and DRAFT Deal created in the same atomic
 * transaction so downstream readers can wire the conversion without PII.
 */
export const LeadConvertedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  contactId: z.uuid(),
  propertyId: z.uuid(),
  dealId: z.uuid(),
  // The lead activities the Qualify re-planned as real activity records on the
  // new deal (IMM-853). The lead-projection carry-over SKIPS exactly these: the
  // re-planned record already carries the step onto the deal and the contact, as
  // an actionable row, so linking the lead projection too would put the same
  // planned step on „Anstehend" twice — once actionable, once an inert twin.
  // Default `[]` so events written before IMM-853 replay unchanged.
  carriedOverActivityIds: z.array(z.uuid()).default([]),
  occurredAt: z.iso.datetime(),
});

export type LeadConvertedPayload = z.infer<typeof LeadConvertedPayloadSchema>;

/**
 * Bus event emitted when a member edits an Owner-Lead's owner section
 * (lead-edit, IMM-227). Ids + actor only — the owner PII is intentionally
 * omitted from the audit trail per ADR 0015.
 */
export const LeadOwnerUpdatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type LeadOwnerUpdatedPayload = z.infer<typeof LeadOwnerUpdatedPayloadSchema>;

/**
 * Bus event emitted when a member edits an Owner-Lead's notes section
 * (lead-edit, IMM-230). Ids + actor only — the notes text is intentionally
 * omitted from the audit trail per ADR 0015.
 */
export const LeadNotesUpdatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type LeadNotesUpdatedPayload = z.infer<typeof LeadNotesUpdatedPayloadSchema>;

/**
 * Bus event emitted when a member edits an Owner-Lead's GDPR legal basis
 * (lead-edit, IMM-231). Ids + actor only per ADR 0015.
 */
export const LeadLegalBasisUpdatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type LeadLegalBasisUpdatedPayload = z.infer<typeof LeadLegalBasisUpdatedPayloadSchema>;

/**
 * Bus event emitted when a member records or corrects an Owner-Lead's intent
 * (lead-edit, IMM-792.1). Ids + actor only per ADR 0015 — the value itself is
 * not included.
 */
export const LeadIntentUpdatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type LeadIntentUpdatedPayload = z.infer<typeof LeadIntentUpdatedPayloadSchema>;

/**
 * Bus event emitted when a member edits an Owner-Lead's address section
 * (lead-edit, IMM-229). Ids + actor only — the address detail is intentionally
 * omitted from the audit trail per ADR 0015.
 */
export const LeadPropertyAddressUpdatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type LeadPropertyAddressUpdatedPayload = z.infer<typeof LeadPropertyAddressUpdatedPayloadSchema>;

/**
 * Bus event emitted when a member edits an Owner-Lead's property section
 * object characteristics (lead-edit, IMM-228). Ids + actor only — the property
 * detail is intentionally omitted from the audit trail per ADR 0015.
 */
export const LeadPropertyUpdatedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type LeadPropertyUpdatedPayload = z.infer<typeof LeadPropertyUpdatedPayloadSchema>;

/**
 * Bus event emitted when a member records a manual note (Notiz) against an
 * Owner-Lead (IMM-259). Unlike the lifecycle events this carries the note
 * `body` so the activity timeline can render the entry without a back-read.
 * Workspace-scoped — leads carry no organization scope (ADR 0008). `noteId`
 * is the `lead_notes` row PK; the activity projection keys its idempotent row
 * on it.
 */
export const LeadNoteRecordedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  noteId: z.uuid(),
  body: z.string(),
  // Plannable fields (IMM-418.1). `state` defaults to DONE and `occurredAt` stays
  // non-null for events published before 418.1 (in-flight outbox rows across the
  // deploy); a planned (OPEN) note carries a `dueAt` and a null `occurredAt`.
  state: LeadActivityStateSchema.default('DONE'),
  occurredAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable().default(null),
  assigneeMemberId: z.uuid().nullable().default(null),
});

export type LeadNoteRecordedPayload = z.infer<typeof LeadNoteRecordedPayloadSchema>;

/**
 * Bus event emitted when a member records a manual communication (call / SMS /
 * WhatsApp / fax / letter) against an Owner-Lead (IMM-259). Carries the
 * direction-aware fields so the timeline renders a direction-aware title.
 * `communicationId` is the `lead_communications` row PK — the activity
 * projection's idempotency key.
 */
export const LeadCommunicationRecordedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  communicationId: z.uuid(),
  type: LeadCommunicationTypeSchema,
  direction: LeadCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  // Plannable fields (IMM-418.1) — see LeadNoteRecordedPayloadSchema. A planned
  // (OPEN) communication carries a `dueAt` and a null `occurredAt`, and must not
  // fire the IMM-417 auto-advance (that keys off a DONE communication only).
  state: LeadActivityStateSchema.default('DONE'),
  occurredAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable().default(null),
  assigneeMemberId: z.uuid().nullable().default(null),
  // „Jetzt festhalten" Ergebnis (IMM-418.2): set for a DONE communication logged
  // with an outcome, null otherwise. Defaults null for events published before
  // this slice (in-flight outbox rows across the deploy).
  outcome: LeadActivityOutcomeSchema.nullable().default(null),
});

export type LeadCommunicationRecordedPayload = z.infer<typeof LeadCommunicationRecordedPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) communication is completed (IMM-418.2)
 * — the `OPEN → DONE` transition. Carries the full communication fields (mirroring
 * LeadCommunicationRecordedPayloadSchema) plus the `outcome`, so the activity
 * projection can AMEND the existing timeline row (keyed on `communicationId`)
 * with the complete body rather than losing its content. `contentEdited` is
 * false — a state transition is not a „bearbeitet" content edit (Decision D3).
 */
export const LeadCommunicationCompletedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  communicationId: z.uuid(),
  type: LeadCommunicationTypeSchema,
  direction: LeadCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  state: LeadActivityStateSchema,
  occurredAt: z.iso.datetime(),
  assigneeMemberId: z.uuid().nullable(),
  outcome: LeadActivityOutcomeSchema.nullable(),
  contentEdited: z.boolean(),
});

export type LeadCommunicationCompletedPayload = z.infer<typeof LeadCommunicationCompletedPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) note is completed (IMM-418.2). Mirrors
 * LeadNoteRecordedPayloadSchema with the completion fields; a note never carries
 * an outcome. The projection amends the existing timeline row (keyed on `noteId`)
 * with the full body. `contentEdited` false — a transition is not an edit (D3).
 */
export const LeadNoteCompletedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  noteId: z.uuid(),
  body: z.string(),
  state: LeadActivityStateSchema,
  occurredAt: z.iso.datetime(),
  assigneeMemberId: z.uuid().nullable(),
  contentEdited: z.boolean(),
});

export type LeadNoteCompletedPayload = z.infer<typeof LeadNoteCompletedPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) communication is rescheduled
 * (IMM-418.4) — „Fällig am" replaced, the activity stays OPEN. Carries the full
 * communication body so the projection can AMEND the existing „Anstehend" row
 * (keyed on `communicationId`) with the new `newDueAt` without losing content.
 * `previousDueAt` + `newDueAt` are the audit trail (Decision D4). `contentEdited`
 * is false — a reschedule is not a „bearbeitet" content edit (Decision D3).
 */
export const LeadCommunicationRescheduledPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  communicationId: z.uuid(),
  type: LeadCommunicationTypeSchema,
  direction: LeadCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  assigneeMemberId: z.uuid().nullable(),
  previousDueAt: z.iso.datetime().nullable(),
  newDueAt: z.iso.datetime(),
  contentEdited: z.boolean(),
});
export type LeadCommunicationRescheduledPayload = z.infer<typeof LeadCommunicationRescheduledPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) note is rescheduled (IMM-418.4). See
 * LeadCommunicationRescheduledPayloadSchema — a note carries no outcome.
 */
export const LeadNoteRescheduledPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  noteId: z.uuid(),
  body: z.string(),
  assigneeMemberId: z.uuid().nullable(),
  previousDueAt: z.iso.datetime().nullable(),
  newDueAt: z.iso.datetime(),
  contentEdited: z.boolean(),
});
export type LeadNoteRescheduledPayload = z.infer<typeof LeadNoteRescheduledPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) communication is cancelled („Absagen",
 * IMM-418.4) — `OPEN → CANCELLED`. A withdrawn intention: the projection RETRACTS
 * the timeline row (keyed on `communicationId`), so the activity appears in
 * neither „Anstehend" nor „Verlauf".
 */
export const LeadCommunicationCancelledPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  communicationId: z.uuid(),
});
export type LeadCommunicationCancelledPayload = z.infer<typeof LeadCommunicationCancelledPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) note is cancelled (IMM-418.4). See
 * LeadCommunicationCancelledPayloadSchema.
 */
export const LeadNoteCancelledPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  noteId: z.uuid(),
});
export type LeadNoteCancelledPayload = z.infer<typeof LeadNoteCancelledPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) communication's Zuständiger is changed
 * (IMM-418.4). Carries the full body so the projection AMENDs the „Anstehend" row
 * (keyed on `communicationId`) with the new assignee, moving it between the
 * members' open lists. `previousAssigneeMemberId` + `newAssigneeMemberId` are the
 * audit trail (Decision D4). `contentEdited` false (Decision D3).
 */
export const LeadCommunicationReassignedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  communicationId: z.uuid(),
  type: LeadCommunicationTypeSchema,
  direction: LeadCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  dueAt: z.iso.datetime().nullable(),
  previousAssigneeMemberId: z.uuid().nullable(),
  newAssigneeMemberId: z.uuid(),
  contentEdited: z.boolean(),
});
export type LeadCommunicationReassignedPayload = z.infer<typeof LeadCommunicationReassignedPayloadSchema>;

/**
 * Bus event emitted when an OPEN (planned) note's Zuständiger is changed
 * (IMM-418.4). See LeadCommunicationReassignedPayloadSchema.
 */
export const LeadNoteReassignedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  noteId: z.uuid(),
  body: z.string(),
  dueAt: z.iso.datetime().nullable(),
  previousAssigneeMemberId: z.uuid().nullable(),
  newAssigneeMemberId: z.uuid(),
  contentEdited: z.boolean(),
});
export type LeadNoteReassignedPayload = z.infer<typeof LeadNoteReassignedPayloadSchema>;

export const LEAD_ASSIGNMENT_TRIGGERS = ['AUTO_CREATION', 'MANUAL_ASSIGNMENT', 'DEACTIVATION'] as const;
export const LeadAssignmentTriggerSchema = z.enum(LEAD_ASSIGNMENT_TRIGGERS);
export type LeadAssignmentTrigger = z.infer<typeof LeadAssignmentTriggerSchema>;

/**
 * Bus event emitted when a lead's responsible-member set changes (IMM-344):
 * the create-time auto-assignment (`AUTO_CREATION`, actor = system), a manual
 * reassignment (`MANUAL_ASSIGNMENT`), or removal on member deactivation
 * (`DEACTIVATION`). Carries the member id sets (ids only, ADR 0015) so the
 * activity-timeline projection records the audit and the notification consumer
 * (IMM-344.2) e-mails the newly-added members. `assignmentChangeId` is the
 * deterministic idempotency key the projection uses. `viaOwnerFallback` marks
 * the create-time Owner fallback so the e-mail can add its fallback note.
 */
export const LeadResponsibilityChangedPayloadSchema = z.object({
  leadId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  assignmentChangeId: z.uuid(),
  previousMemberIds: z.array(z.uuid()),
  newMemberIds: z.array(z.uuid()),
  trigger: LeadAssignmentTriggerSchema,
  viaOwnerFallback: z.boolean(),
  occurredAt: z.iso.datetime(),
});

export type LeadResponsibilityChangedPayload = z.infer<typeof LeadResponsibilityChangedPayloadSchema>;
